// Машина состояний детской сессии (FR-KID-04/05, FR-QUIZ-06): разминка → новое → задания → награда.
// Лимит — по реальному времени: сворачивание приложения его не останавливает; задание доигрывается ≤ 1 мин.

export type Task = { scenarioId: string; quizId: string; variant: "original" | "mirror"; warmup?: boolean };

export type Plan = { warmup: Task | null; scenarioId: string; tasks: Task[] };

export type Phase = "start" | "warmup" | "scenario" | "task" | "reward" | "end";

export type SessionState = {
  phase: Phase;
  plan: Plan;
  taskIndex: number;
  startedAt: number | null;
  limitMs: number;
  /** Время, после которого задание прерывается (лимит + доигрывание). */
  hardStopAt: number | null;
  stars: number;
  endReason: "done" | "limit" | null;
};

export type SessionEvent =
  | { type: "START"; now: number }
  | { type: "WARMUP_DONE"; star: boolean }
  | { type: "SCENARIO_DONE" }
  | { type: "TASK_DONE"; star: boolean; now: number }
  | { type: "REWARD_DONE" }
  | { type: "TICK"; now: number };

export const GRACE_MS = 60_000;

export function initSession(plan: Plan, limitMin: number): SessionState {
  return { phase: "start", plan, taskIndex: 0, startedAt: null, limitMs: limitMin * 60_000, hardStopAt: null, stars: 0, endReason: null };
}

function limitReached(s: SessionState, now: number): boolean {
  return s.startedAt !== null && now - s.startedAt >= s.limitMs;
}

export function step(s: SessionState, e: SessionEvent): SessionState {
  switch (e.type) {
    case "START":
      if (s.phase !== "start") return s;
      return { ...s, startedAt: e.now, phase: s.plan.warmup ? "warmup" : "scenario" };
    case "WARMUP_DONE":
      if (s.phase !== "warmup") return s;
      return { ...s, stars: s.stars + (e.star ? 1 : 0), phase: "scenario" };
    case "SCENARIO_DONE":
      if (s.phase !== "scenario") return s;
      return { ...s, phase: s.plan.tasks.length ? "task" : "reward" };
    case "TASK_DONE": {
      if (s.phase !== "task") return s;
      const stars = s.stars + (e.star ? 1 : 0);
      const next = s.taskIndex + 1;
      // По лимиту — после доигранного задания сразу к завершению (с наградой за сделанное).
      if (limitReached(s, e.now)) return { ...s, stars, phase: "reward", endReason: "limit" };
      if (next >= s.plan.tasks.length) return { ...s, stars, phase: "reward", endReason: "done" };
      return { ...s, stars, taskIndex: next };
    }
    case "REWARD_DONE":
      return s.phase === "reward" ? { ...s, phase: "end", endReason: s.endReason ?? "done" } : s;
    case "TICK": {
      if (s.phase === "start" || s.phase === "end" || s.phase === "reward" || !limitReached(s, e.now)) return s;
      if (s.phase === "task") {
        const hardStopAt = s.hardStopAt ?? e.now + GRACE_MS;
        if (e.now >= hardStopAt) return { ...s, phase: "reward", endReason: "limit", hardStopAt };
        return { ...s, hardStopAt };
      }
      // Сценарий или разминка — мягкое завершение сразу.
      return { ...s, phase: "reward", endReason: "limit" };
    }
  }
}

/** Попытки задания (FR-QUIZ-06): 1 — последствие и «ещё раз», 2 — подсказка, 3 — показ правильного ответа. */
export type AttemptOutcome = { action: "praise" | "retry" | "hint" | "showAnswer"; nextAttempt: number | null };

export function afterAnswer(attempt: number, grade: "great" | "ok" | "bad"): AttemptOutcome {
  if (grade !== "bad") return { action: "praise", nextAttempt: null };
  if (attempt === 1) return { action: "retry", nextAttempt: 2 };
  if (attempt === 2) return { action: "hint", nextAttempt: 3 };
  return { action: "showAnswer", nextAttempt: null };
}
